import { IWebsiteIntegration } from "@promotron/website-integration-types";
import HttpClient from "../http-client";
import LoadingIndication from "../loading-indication";
import { devLog, displayError, localize } from "../common";
import { getTronDesignerAsync, getCurrentTronLogoUserGuid, getTronLogoResourceBase, buildTronLogoUrl } from "./tron-designer";

// Addendum 2 §2.1 — the cart's logo fallback on TD-integrated shops. The "select / upload logo" action on
// a print motive set opens the TronDesigner logo gallery (upload new / pick from the customer's gallery)
// instead of the old TronLogo one. The selection is persisted on the cart item:
//   - the TD logo guid — so a later "Create design" preloads the editor with the native TD logo,
//   - the logo file itself (fetched from TD and re-uploaded, best-effort) — so the order carries the
//     artwork for fulfilment exactly like a manual file upload would.
// The action is only rendered while no Print Job exists for the parent print set (server-side gating in
// _CartItemPartial); once a design exists the logo is managed inside the design.
//
// Logo events (TronDesigner develop @7f8e0377, TronDesignerApp.tsx / useLogoApi.ts / print-job-builder hooks.ts):
//   - "selected" is the gallery dialog's only outcome: an existing logo picked OR a new one uploaded/replaced
//     there. TD dispatches the dialog's resolved ILogoDTO as detail; we read only logoGuid and fetch the file
//     URLs via td.getLogo() (with retries: the SDK sets the current logo async).
//   - "created" fires when a logo is uploaded in the editor and placed into a printing — detail is the
//     full ILogoDTO; handled only while a cart gallery pick is pending (consumeActiveSelection).
//   - "updated" fires for a rename (gallery item / logo info) or the editor re-activating an edited logo —
//     never for an upload — so it is not handled (Q-12: a rename assigned the logo to the cart item).

// Extensions the cart upload endpoint accepts (FormFileExtensions.ValidateFile) — anything else is
// persisted guid-only rather than bounced by the server.
const UPLOADABLE_EXTENSIONS = ["pdf", "eps", "ai", "cdr", "svg", "png", "tiff", "psd"];

// ILogoDTO.filesDescription formats arrive as the numeric FileFormats enum (SVG=0 … SLA=9), while the
// lower-level DTOs use the string form — normalize both to a lowercase extension.
const FILE_FORMAT_NAMES = ["svg", "pdf", "ai", "eps", "cdr", "png", "jpg", "webp", "tiff", "sla"];

// The subset of ILogoDTO this module reads — shared by the created-event detail and getLogo().
// The converted*/preview* derivatives are rendered rasters (they share convertedFormat / carry
// previewFormat); `original` is the raw upload and may be a vector an <img> cannot paint.
interface LogoFileInfo {
    logoGuid?: string;
    filesDescription?: {
        original?: string;
        originalFormat?: unknown;
        converted?: string;
        convertedFormat?: unknown;
        convertedWhite?: string;
        convertedBlack?: string;
        preview?: string;
        previewFormat?: unknown;
    };
}

let _activeCartItemId: string | null = null;
let _activeLogoBox: HTMLElement | null = null;
// Bumped on every openGalleryFor capture — lets the gallery's deferred onClose cleanup detect that a
// NEWER capture replaced the one it was scheduled for, so it never wipes a fresh selection context.
let _activeCaptureSeq = 0;
// How long after the gallery closes before a still-unconsumed capture is discarded. The "selected"
// logo event races the close (the selection is what closes the dialog), so the clear is deferred just
// long enough for that event to win; after this window a leftover capture can only be stale — and a
// later global logo event (e.g. from the editor) must NOT consume it and POST to the wrong cart item.
const GALLERY_CLOSE_CLEAR_DELAY_MS = 3000;
let _eventsSubscribed = false;
let _reload: () => void = () => window.location.reload();

export function initTronDesignerCartLogoGallery(reload?: () => void): void {
    if (reload) {
        _reload = reload;
    }

    initTlLogoThumbs();
    void initTdLogoThumbs();

    document.querySelectorAll<HTMLElement>("[data-td-logo-action]").forEach(el => {
        if (el.dataset.tdLogoActionBound) return;
        el.dataset.tdLogoActionBound = "1";

        el.addEventListener("click", evt => {
            evt.preventDefault();
            const cartItemId = el.dataset.cartItemId;
            if (!cartItemId) {
                return;
            }
            void openGalleryFor(cartItemId, el);
        });
    });

    guardTronLogoGalleryButtons();
}

// The TronLogo gallery buttons (data-tl-action="OpenGallery") are bound by the external TronLogo script.
// When that script is missing or failed to load (e.g. a 401 for the shop's API key), the click would do
// nothing at all, so the customer is told instead. While the engine is present this listener stays out
// of the way and TronLogo handles the click.
function guardTronLogoGalleryButtons(): void {
    document.querySelectorAll<HTMLElement>('.cart-card__logo-btn[data-tl-action="OpenGallery"]').forEach(el => {
        if (el.dataset.tlGalleryGuardBound) return;
        el.dataset.tlGalleryGuardBound = "1";

        el.addEventListener("click", evt => {
            if (window.tronlogo !== undefined) {
                return;
            }
            evt.preventDefault();
            displayError(localize("TlLogoGalleryUnavailable"));
        });
    });
}

// TronLogo logos have no server-renderable image URL (the stored reference points at the DTP page),
// but the SAME user-scoped logo-upload URL handed to the TD editor serves the original file — which the
// browser can render directly for raster/SVG uploads. Resolved here because only the client knows the
// TL user guid (window.tron.User, loaded async — hence the retry). Non-renderable originals (EPS/PDF)
// simply never fire onload and the placeholder stays. A linked design's artwork always wins: the image
// is only inserted while the box still shows the placeholder.
// Recovery hook for a server-rendered logo <img> that failed (e.g. a stale persisted URL that now
// 404s): the view's onerror calls this so the box gets a second chance through whichever relation
// it has — TL (guid+base attrs) or TD (cart logo guid attr). Both attr sets render unconditionally
// exactly for this: stored-URL data problems must never lock out their own recovery.
export function retryCartLogoThumb(box: HTMLElement): void {
    delete box.dataset.tlThumbBound;
    delete box.dataset.tdThumbBound;
    initTlLogoThumbs();
    void initTdLogoThumbs();
}

// ── Case 4 of the cart logo matrix: TD integration active, logo uploaded via the TD LOGO GALLERY,
//    no print job. The logo lives in TRONDESIGNER under the TD customer — so it is fetched from TD
//    (SDK setLogo/getLogo, same pattern the persist flow uses), never via TronLogo URLs (those are
//    cases 1/2 and 404 for TD-owned logos — field-verified). On success the resolved URL is also
//    persisted back to the cart item (fire-and-forget), self-healing stale/poisoned stored URLs so
//    the next render is a plain server-side <img>. Boxes are processed sequentially because setLogo
//    mutates the SDK's global current-logo state. ──
// TD API access from the script tag TronDesignerHelper renders (data-td-api + data-td-api-key).
function getTdApiConfig(): { api: string; key: string } | null {
    const script = document.querySelector<HTMLScriptElement>("script[data-td-api-key]");
    const api = script?.dataset.tdApi;
    const key = script?.dataset.tdApiKey;
    return api && key ? { api: api.replace(/\/+$/, ""), key } : null;
}

// The one deterministic way to get a TD logo image (field-verified):
// GET {api}/logos/{guid}/preview with the TronDesigner-ApiKey header returns the rasterized PNG.
// filesDescription cannot be used for display — TD returns bare storage FILENAMES there, not URLs.
async function fetchTdLogoPreviewBlob(logoGuid: string): Promise<Blob | null> {
    const cfg = getTdApiConfig();
    if (!cfg) {
        return null;
    }
    try {
        const resp = await fetch(`${cfg.api}/logos/${encodeURIComponent(logoGuid)}/preview`, {
            headers: { "TronDesigner-ApiKey": cfg.key },
        });
        if (!resp.ok) {
            console.warn("TronDesigner: logo preview endpoint returned", resp.status, { logoGuid });
            return null;
        }
        return await resp.blob();
    } catch (err) {
        console.warn("TronDesigner: logo preview fetch failed.", { logoGuid, err });
        return null;
    }
}

export async function initTdLogoThumbs(): Promise<void> {
    const boxes = Array.from(document.querySelectorAll<HTMLElement>("[data-td-design-thumb][data-td-cart-logo-guid]"))
        .filter(box => !box.dataset.tdThumbBound && !box.querySelector("img"));
    if (!boxes.length) {
        return;
    }
    boxes.forEach(box => box.dataset.tdThumbBound = "1");

    for (const box of boxes) {
        const logoGuid = box.dataset.tdCartLogoGuid;
        if (!logoGuid) {
            continue;
        }

        const blob = await fetchTdLogoPreviewBlob(logoGuid);
        if (!blob) {
            continue; // warning already logged with the concrete failure
        }

        // Linked-design artwork (case 3) wins — only fill a box that is still the placeholder. Build the
        // <img> (and the object URL) only when we actually append it, so a box that already won stays
        // untouched and we don't leak a throw-away object URL. Revoke on load to free the blob.
        if (!box.querySelector("img")) {
            const img = document.createElement("img");
            img.alt = "";
            const objectUrl = URL.createObjectURL(blob);
            img.addEventListener("load", () => URL.revokeObjectURL(objectUrl), { once: true });
            img.src = objectUrl;
            box.classList.remove("cart-card__design-thumb--empty");
            box.textContent = "";
            box.appendChild(img);
        }

        // Self-heal: upload the preview blob as the item's logo file — from the next render on, the
        // shop serves it itself (/api/cart/{id}/logo-image) with no TD round-trip and no stored-URL
        // fragility. Overwrites any stale/poisoned display URL along the way. Best-effort and silent:
        // pass a no-op onError so a transient/CORS/404 failure doesn't pop a generic error modal (and
        // doesn't leave the void-discarded promise as an unhandled rejection) during normal cart render.
        const cartItemId = box.closest<HTMLElement>(".cart-preview-motive-set")?.dataset.productId;
        if (cartItemId) {
            const body = new FormData();
            body.append("file", new File([blob], `trondesigner-logo-${logoGuid}-preview.png`, { type: blob.type || "image/png" }));
            void new HttpClient().fetch(
                `/api/cart/${encodeURIComponent(cartItemId)}/file/trondesignerlogo/${encodeURIComponent(logoGuid)}`,
                { method: "POST", body },
                () => { /* best-effort self-heal — stay silent on failure */ }
            );
        }
    }
}

export function initTlLogoThumbs(attempt = 0): void {
    const boxes = Array.from(document.querySelectorAll<HTMLElement>("[data-td-design-thumb][data-tl-logo-guid]"))
        .filter(box => !box.dataset.tlThumbBound && !box.querySelector("img"));
    if (!boxes.length) {
        return;
    }

    // Base priority: the box's own server-rendered attribute (works on TL-native shops with no TD
    // script tag), then the TD script tag's shadow-TL base. The user guid comes live from the TL
    // engine or from the localStorage cache populated on any page where the engine loaded.
    const base = boxes[0].dataset.tlLogoBase || getTronLogoResourceBase();
    const userGuid = getCurrentTronLogoUserGuid();
    if (!base || !userGuid) {
        // The TL engine loads async; give it a few seconds before giving up.
        if (attempt < 20) {
            window.setTimeout(() => initTlLogoThumbs(attempt + 1), 250);
        }
        return;
    }

    boxes.forEach(box => {
        box.dataset.tlThumbBound = "1";
        const logoGuid = box.dataset.tlLogoGuid;
        if (!logoGuid) {
            return;
        }

        const img = document.createElement("img");
        img.alt = "";
        img.addEventListener("load", () => {
            if (box.classList.contains("cart-card__design-thumb--empty")) {
                box.classList.remove("cart-card__design-thumb--empty");
                box.textContent = "";
                box.appendChild(img);
            }
        }, { once: true });
        img.src = buildTronLogoUrl(base, userGuid, logoGuid);
    });
}

async function openGalleryFor(cartItemId: string, trigger: HTMLElement): Promise<void> {
    // The SDK can take seconds to become ready — scope a loading overlay to the motive set so the click
    // gives immediate feedback.
    const overlay = trigger.closest<HTMLElement>(".cart-preview-motive-set") ?? document.body;
    // Remember which motive set opened the gallery — the logo events carry only the logo. Capture this
    // synchronously, BEFORE awaiting the (possibly slow) SDK load, so two quick clicks on different
    // motive sets can't interleave and attribute the logo to whichever finished awaiting last.
    _activeCartItemId = cartItemId;
    _activeLogoBox = trigger.closest<HTMLElement>(".cart-preview-motive-set")?.querySelector<HTMLElement>("[data-td-design-thumb]") ?? null;
    _activeCaptureSeq++;
    LoadingIndication.show(overlay);
    try {
        const td = await getTronDesignerAsync();
        subscribeGalleryEvents(td);
        td.openLogoGallery({
            mode: "dialog",
            // Fix: the capture was never cleared when the gallery closed WITHOUT a selection, so a later
            // global logo event (e.g. an upload inside the editor) consumed the stale id and stored the
            // logo on the wrong cart item. Clear it on close — deferred, because the "selected" event
            // races the close; the seq guard keeps a re-opened gallery's fresh capture untouched.
            onClose: () => {
                const seqAtClose = _activeCaptureSeq;
                window.setTimeout(() => {
                    if (_activeCaptureSeq === seqAtClose) {
                        _activeCartItemId = null;
                        _activeLogoBox = null;
                    }
                }, GALLERY_CLOSE_CLEAR_DELAY_MS);
            }
        });
    } catch (err) {
        console.error("TronDesigner cart logo gallery failed to open:", err);
        displayError(localize("TdLogoGalleryFailed"));
    } finally {
        LoadingIndication.hide(overlay);
    }
}

function subscribeGalleryEvents(td: IWebsiteIntegration): void {
    // One global subscription set; the active cart item id scopes each event (same pattern as the
    // print-job save handlers in tron-designer.ts).
    if (_eventsSubscribed) return;
    _eventsSubscribed = true;

    // Picking an EXISTING logo in the gallery.
    td.subscribeDesignerEvent("logo", "selected", event => {
        const logoGuid = event.detail?.logoGuid;
        const { cartItemId, box } = consumeActiveSelection();
        if (!logoGuid || !cartItemId) {
            return;
        }
        devLog("TronDesigner: cart logo selected from gallery", { cartItemId, logoGuid });
        void persistSelection(td, cartItemId, logoGuid, null, box);
    });

    // A logo uploaded in the EDITOR ("created") — the event detail is the full logo DTO.
    const onUploaded = (event: { entityGuid: string; detail?: unknown }) => {
        const dto = event.detail as LogoFileInfo | undefined;
        const logoGuid = dto?.logoGuid ?? event.entityGuid;
        const { cartItemId, box } = consumeActiveSelection();
        if (!logoGuid || !cartItemId) {
            return;
        }
        devLog("TronDesigner: cart logo uploaded in gallery", { cartItemId, logoGuid });
        void persistSelection(td, cartItemId, logoGuid, dto ?? null, box);
    };
    td.subscribeDesignerEvent("logo", "created", onUploaded);
    // Deliberately NOT "logo updated": TD fires it when the customer renames a logo (or saves an edited one)
    // in the gallery — not a pick. Handling it assigned the renamed logo to the open cart item and consumed
    // the active selection, so the customer's actual pick afterwards was ignored (Q-12). A pick or an
    // upload in the gallery arrives as "selected".
}

// The gallery stays open while the customer works — only the FIRST logo event after our open is the
// answer to "pick a logo for this cart item"; consuming the id also keeps unrelated logo events
// (e.g. from the editor) from re-triggering the cart POST. The box (the thumb the click came from) is
// consumed alongside so persistSelection can put a loading overlay on it.
function consumeActiveSelection(): { cartItemId: string | null; box: HTMLElement | null } {
    const cartItemId = _activeCartItemId;
    const box = _activeLogoBox;
    _activeCartItemId = null;
    _activeLogoBox = null;
    return { cartItemId, box };
}

// The thumb box for a cart item — fallback for the loading overlay when the click origin wasn't captured
// (e.g. an event fired without a preceding openGalleryFor). data-product-id on the motive set === cartItemId.
function findCartLogoBox(cartItemId: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(
        `.cart-preview-motive-set[data-product-id="${cartItemId}"] [data-td-design-thumb]`
    );
}

// Formats a browser can paint in an <img> — used to pick the display URL sent alongside the selection.
const DISPLAYABLE_EXTENSIONS = ["png", "jpg", "webp", "svg", "gif"];

// Best displayable TD file URL from the logo DTO — the server persists it as the thumbnail fallback
// for when the file re-upload fails (fetch() needs CORS; a plain <img> does not). Prefer the rendered
// rasters (converted / preview derivatives): they are web-safe by construction, whereas `original` is
// often a vector (EPS/PDF/AI) an <img> cannot paint — trying it last stops a raster preview from being
// skipped in favour of an unrenderable original. This is what makes the cart thumbnail reliable enough
// that the guid-only fallback (no display URL at all) is virtually never reached.
function pickDisplayUrl(info: LogoFileInfo | null): string | null {
    const fd = info?.filesDescription;
    if (!fd) {
        return null;
    }
    const candidates = [
        { url: fd.converted, format: normalizeFormat(fd.convertedFormat) },
        { url: fd.preview, format: normalizeFormat(fd.previewFormat) },
        { url: fd.convertedWhite, format: normalizeFormat(fd.convertedFormat) },
        { url: fd.convertedBlack, format: normalizeFormat(fd.convertedFormat) },
        { url: fd.original, format: normalizeFormat(fd.originalFormat) },
    ];
    for (const c of candidates) {
        if (c.url && /^https?:\/\//i.test(c.url) && c.format && DISPLAYABLE_EXTENSIONS.includes(c.format)) {
            return c.url;
        }
    }
    return null;
}

async function persistSelection(td: IWebsiteIntegration, cartItemId: string, logoGuid: string, dto: LogoFileInfo | null, box: HTMLElement | null): Promise<void> {
    // Immediate feedback: spinner on the thumb the click came from. The gallery dialog closes on the
    // selection, so the overlay is visible right away and stays until _reload() swaps in the new thumb.
    const loadingBox = box ?? findCartLogoBox(cartItemId);
    if (loadingBox) {
        LoadingIndication.show(loadingBox);
    }

    try {
        const body = new FormData();

        // Fast path (the common one): the deterministic preview endpoint. It is the only image source
        // that resolves in current TD — filesDescription carries bare storage filenames, so the
        // URL-based DTO paths never yield anything and only cost seconds of setLogo/getLogo/derivative
        // polling. Fetching the preview up front is what makes the post-upload refresh near-instant.
        const previewBlob = await fetchTdLogoPreviewBlob(logoGuid);
        let file: File | null = previewBlob
            ? new File([previewBlob], `trondesigner-logo-${logoGuid}-preview.png`, { type: previewBlob.type || "image/png" })
            : null;

        // Slow fallback, only reached when the preview endpoint is unavailable (no TD script tag or a
        // transient failure): resolve the file/thumbnail URL from the logo DTO the way we used to,
        // polling for TronDesigner's async raster derivatives on fresh uploads.
        if (!file) {
            let info = await resolveLogoFileInfo(td, logoGuid, dto);
            file = await tryFetchLogoFile(td, logoGuid, info);
            if (!file && !pickDisplayUrl(info)) {
                for (let attempt = 0; attempt < 12; attempt++) {
                    await new Promise(resolve => window.setTimeout(resolve, 800));
                    const current = await td.getLogo() as LogoFileInfo | undefined;
                    if (current?.logoGuid === logoGuid && pickDisplayUrl(current)) {
                        info = current;
                        file = await tryFetchLogoFile(td, logoGuid, info);
                        break;
                    }
                }
            }
            // Display URL for the cart thumbnail: TD's own file URLs only (rasterized preview first). NO
            // TL-URL fallback here — a TD gallery logo is owned by the TD CUSTOMER, not the TL browser
            // user, so a user/{tlUserGuid}/logo-upload/{tdLogoGuid} URL 404s (verified in the field) and
            // would poison the stored thumbnail with a permanently broken image.
            const displayUrl = pickDisplayUrl(info);
            if (displayUrl) {
                body.append("logoUrl", displayUrl);
            } else {
                console.warn("TronDesigner: no display URL resolvable for the cart logo thumbnail — placeholder will remain.", { logoGuid, filesDescription: info?.filesDescription });
            }
        }

        if (file) {
            body.append("file", file, file.name);
        }

        const response = await new HttpClient().fetch(
            `/api/cart/${encodeURIComponent(cartItemId)}/file/trondesignerlogo/${encodeURIComponent(logoGuid)}`,
            { method: "POST", body }
        );

        if (response?.ok) {
            _reload(); // the AJAX cart reload replaces this box (overlay and all) with the new thumb
        } else {
            console.error("TronDesigner: failed to store the selected cart logo", response?.status);
            if (loadingBox) {
                LoadingIndication.hide(loadingBox);
            }
        }
    } catch (err) {
        console.error("TronDesigner: failed to store the selected cart logo", err);
        if (loadingBox) {
            LoadingIndication.hide(loadingBox);
        }
    }
}

// Resolve the logo DTO carrying the file URLs: the upload events deliver it directly; a gallery
// selection needs td.getLogo(), which the SDK updates asynchronously after the selection — retry
// briefly until it reflects the selected guid.
async function resolveLogoFileInfo(td: IWebsiteIntegration, logoGuid: string, dto: LogoFileInfo | null): Promise<LogoFileInfo | null> {
    if (dto?.filesDescription) {
        return dto;
    }

    // A gallery SELECTION only carries the guid, and the SDK updates its "current logo" asynchronously —
    // polling getLogo() raced and frequently exhausted its retries, leaving the cart thumbnail with no
    // display URL at all. setLogo() makes the read deterministic: force the current logo first, then read.
    try {
        await td.setLogo(logoGuid);
    } catch {
        // fall through to polling — setLogo may reject for gallery logos already current
    }

    for (let attempt = 0; attempt < 10; attempt++) {
        const current = await td.getLogo() as LogoFileInfo | undefined;
        if (current?.logoGuid === logoGuid) {
            return current;
        }
        await new Promise(resolve => window.setTimeout(resolve, 400));
    }

    console.warn("TronDesigner: getLogo never reflected the selected logo — thumbnail data unavailable.", { logoGuid });
    return null;
}

// Fetch the logo's file from TronDesigner so it can be re-uploaded to the shop for the order/fulfilment
// flow. Best-effort by design: on any miss (DTO unavailable, no absolute URL, unsupported format, CORS)
// the selection is persisted guid-only — the cart still shows the logo as uploaded (state keys off the
// guid) and the editor preload still works; only the order attachment is absent. Misses are logged as
// warnings so a systematic environment problem (e.g. TD URLs not fetchable) is visible in the field.
async function tryFetchLogoFile(td: IWebsiteIntegration, logoGuid: string, info: LogoFileInfo | null): Promise<File | null> {
    try {
        if (!info?.filesDescription) {
            console.warn("TronDesigner: logo file info unavailable — storing the logo guid only.", { logoGuid });
            return null;
        }

        // Prefer the original upload (print quality) over the converted derivative.
        const candidates: Array<{ url?: string; format: string | null }> = [
            { url: info.filesDescription.original, format: normalizeFormat(info.filesDescription.originalFormat) },
            { url: info.filesDescription.converted, format: normalizeFormat(info.filesDescription.convertedFormat) },
        ];

        for (const candidate of candidates) {
            const extension = candidate.format;
            if (!candidate.url || !/^https?:\/\//i.test(candidate.url) || !extension || !UPLOADABLE_EXTENSIONS.includes(extension)) {
                continue;
            }

            const response = await fetch(candidate.url);
            if (!response.ok) {
                continue;
            }

            const blob = await response.blob();
            return new File([blob], `trondesigner-logo-${logoGuid}.${extension}`, { type: blob.type });
        }

        console.warn("TronDesigner: no fetchable logo file — storing the logo guid only.", { logoGuid, filesDescription: info.filesDescription });
        return null;
    } catch (err) {
        console.warn("TronDesigner: logo file fetch failed — storing the logo guid only.", { logoGuid, err });
        return null;
    }
}

function normalizeFormat(format: unknown): string | null {
    if (typeof format === "number") {
        return FILE_FORMAT_NAMES[format] ?? null;
    }
    if (typeof format === "string" && format.length) {
        return format.toLowerCase();
    }
    return null;
}
